export const translations = {
    en: {
        // Navbar
        nav: {
            problem: "Problem",
            solution: "Solution",
            zelliAngel: "Zelli Angel",
            technology: "Technology",
            market: "Market",
            team: "Team",
            pitchDeck: "PITCH DECK",
            investNow: "INVEST NOW",
            subtitle: "NATIONAL SAFETY GRID"
        },
        footer: {
            rights: "© 2026 NAJA AI. All rights reserved.",
            contact: "Contact",
            pitchDeck: "Pitch Deck",
            builtWith: "Built with ❤️ in Morocco"
        },
        loader: {
            text: "LOADING NAJA GRID..."
        },
        // Hero
        hero: {
            loading: "LOADING NAJA GRID...",
            alert: "⚠️ ACTIVE: FLASH FLOOD WARNING - GHARB PLAIN / KSAR EL KBIR",
            title: { naja: "NAJA", ai: " AI" },
            subtitle: "THE NATIONAL SAFETY GRID",
            tagline: { track: "We Track.", predict: "We Predict.", protect: "We Protect." },
            description: "Morocco's first Unified Safety Operating System — connecting smartphones, wearables, and routers to protect families, predict disasters, and locate missing persons in real time.",
            capabilities: {
                track: { title: "WE TRACK", subtitle: "Real-Time Location", desc: "Cross-device sync with sub-50ms latency. Biometric monitoring for heart rate, falls, and stress.", features: ['Mesh Networking', 'Offline Mode', 'Wearable Sync'] },
                predict: { title: "WE PREDICT", subtitle: "AI Trained on Morocco", desc: "Proprietary models forecast floods in specific valleys, crowd dynamics for World Cup 2030.", features: ['Flood Prediction', 'Crowd Analysis', 'Truth Score™'] },
                protect: { title: "WE PROTECT", subtitle: "Automated Response", desc: "Instant alerts, search grids for missing children, emergency protocols that activate in seconds.", features: ['One-Touch SOS', 'Zombie Mode', 'Smart Checkpoints'] }
            },
            cta: { partner: "PARTNER WITH US", explore: "EXPLORE THE VISION" },
            partner: "PARTNERING WITH INWI TO BUILD MOROCCO'S DIGITAL NERVOUS SYSTEM",
            scroll: "Discover More"
        },
        // Checkpoints
        checkpoints: {
            tag: "SMART SAFE CHECKPOINTS",
            titlePrefix: "Your Compass",
            titleHighlight: "During Chaos",
            description: "In a crisis, people panic. NAJA AI transforms chaos into order by dynamically designating verified safe checkpoints with real-time capacity tracking.",
            verified: " verified safe checkpoints",
            liveFeed: "LIVE SATELLITE FEED",
            safestPattern: "Safest Pattern Detected",
            fast: "FAST",
            nearbySanctuaries: "Nearby Sanctuaries",
            updated: "UPDATED 2S AGO",
            walk: "WALK",
            currentCapacity: "Current Capacity",
            startNav: "Start Navigation",
            entryPass: "Show Entry Pass",
            you: "You",
            status: {
                open: "Open & Safe",
                nearing: "Nearing Capacity",
                full: "Full / Route Blocked",
                available: "Available",
                filling: "Filling Fast",
                capacity: "At Capacity",
                mesh: "Mesh Network Active"
            }
        },
        // App Simulation
        simulation: {
            header: "TACTICAL MAP",
            hazard: "Hazard Zone",
            safe: "Safe Zone",
            sanctuary: "SANCTUARY",
            you: "You",
            squad: "SQUAD",
            actions: {
                navigate: "Navigate",
                report: "Report",
                findSquad: "Find Squad",
                broadcast: "Broadcast"
            },
            mvpReady: "MVP READY",
            titlePrefix: "See the ",
            titleHighlight: "App in Action",
            description: "Our MVP is live and functional. Explore the tactical map, family protection grid, and AI-powered survival guides that will save Moroccan lives.",
            squadStatus: "SQUAD STATUS",
            survivalGuides: "SURVIVAL GUIDES",
            features: {
                map: { title: "Tactical Hazard Map", desc: "Real-time visualization of seismic, thermal, and meteorological threats. Official data fused with verified crowd reports." },
                squad: { title: "Squad Protection Grid", desc: "Track family members with low-bandwidth 'Pulse' signals. Works via Bluetooth Mesh when cellular networks are down." },
                ai: { title: "Gemini AI Guardian", desc: "A survival expert in your pocket. Provides hyper-local, dialect-aware (Darija) advice during chaos." },
                offline: { title: "Offline Mode", desc: "Critical survival protocols cached locally. Works without internet when you need it most." }
            }
        },
        problem: {
            badge: "THE PROBLEM",
            titlePrefix: "Fragmented ",
            titleHighlight: "Safety Systems",
            description: "Morocco faces recurring disasters with reactive systems that cost lives. The Gharb floods and Atlas earthquake revealed critical gaps in our national preparedness.",
            realDisasters: "REAL DISASTERS",
            rootCauses: "ROOT CAUSES",
            impact: {
                title: "THE COST OF INACTION",
                text: "Every disaster reveals the same pattern: late warnings, chaotic evacuations, separated families, preventable losses. Morocco deserves better. We built NAJA AI to be the answer."
            },
            cases: {
                ksar: {
                    title: 'Ksar El Kbir: The "Ghost Town"',
                    location: 'Larache / Gharb Plain, Morocco',
                    desc: 'Oued El Makhazine Dam reached 166% capacity. Emergency release flooded the entire Gharb plain. The city became a "Ghost Town" as 85% of residents fled in panic.'
                },
                gharb: {
                    title: 'Gharb Plain Dam Crisis',
                    location: 'Oued El Makhazine Dam',
                    desc: 'The dam release was inevitable, but the damage wasn\'t. Without early warning systems, farmers had no time to move livestock or equipment to higher ground.'
                },
                atlas: {
                    title: 'Al Haouz Earthquake',
                    location: 'Atlas Mountains, Morocco',
                    desc: 'A 6.8 magnitude earthquake struck the Atlas Mountains at 11:11 PM. Remote villages were cut off for days. The tragedy that inspired NAJA AI.'
                }
            },
            causes: {
                goldenHour: { title: "The Golden Hour Lost", desc: "Over 2,000 children reported missing in Morocco each year (DGSN 2025) – most without digital search tools." },
                dataSilos: { title: "Data Silos", desc: "Biometrics, location, and alerts never sync across devices. Families are blind when minutes matter." },
                noGrid: { title: "No National Grid", desc: "Existing infrastructure (radars, cameras) is isolated from citizens. No unified alert system." },
                aiFail: { title: "Generic AI Fails", desc: "International models don't understand local flood patterns, crime hotspots, or health trends." },
                chaos: { title: "Information Chaos", desc: "During disasters, citizens face unverified news and fake reports. No trusted source for guidance." }
            }
        },
        solution: {
            badge: "THE SOLUTION",
            titlePrefix: "NAJA AI ",
            titleHighlight: "Ecosystem",
            description: "The first Unified Safety Operating System for Morocco — connecting all your devices into one protective network.",
            categories: {
                track: "WE TRACK",
                predict: "WE PREDICT",
                protect: "WE PROTECT"
            },
            cta: "Meet Zelli Angel, Our AI Guardian",
            caseStudy: {
                title: "Case Study: Ksar El Kbir Reimagined",
                subtitle: "What if NAJA AI was active during the February 2026 floods?",
                phase1: { title: "Predictive Intelligence", desc: "NAJA connected to ABHS sensors detects inflow spikes. Simulation engine predicts Sector B flooding." },
                phase2: { title: "Hyper-Local Notification", desc: "Zelli Angel initiates Flash Calls to all phones in Red Zone with automated voice in Darija." },
                phase3: { title: "Tactical Evacuation", desc: "NAJA App switches to Evac Mode. 5,000+ people directed to 3 checkpoints in 45 minutes." },
                result: { title: "Simulated Result with NAJA AI", quote: "\"With NAJA AI, a crisis of chaos becomes a managed migration.\"" }
            }
        },
        zelli: {
            badge: "ZELLI ANGEL AI",
            titlePrefix: "The Intelligent ",
            titleHighlight: "Heart of NAJA",
            description: "Not just a chatbot — a proactive guardian that communicates in your language via voice and text, 24/7.",
            features: {
                guardian: { title: "24/7 Personal Guardian", desc: "Monitors threats and sends personalized voice alerts in your language.", example: "\"Aymane, a flood is expected in your area in 2 hours. I've prepared a safe route...\"" },
                mass: { title: "Mass Communication", desc: "Simultaneously calls or messages thousands of affected users with Flash Call API.", example: "Bypass \"Do Not Disturb\" mode to wake residents during emergencies." },
                evac: { title: "Evacuation Guidance", desc: "Converts to \"Evac Mode\" during disasters, guiding each user step-by-step.", example: "Dynamic rerouting based on real-time conditions to Smart Safe Checkpoints." },
                whatsapp: { title: "WhatsApp Integration", desc: "Text \"Fayn nemshi?\" and get instant voice note with safest route.", example: "No app required for basic guidance — works on any phone with WhatsApp." }
            },
            demo: {
                title: "Live Conversation Demo",
                subtitle: "Zelli Angel responding to a flood alert",
                userMsg: "Fayn nemshi? (Where should I go?)",
                aiMsg: "\"Aymane, there's a flood warning for your area. The safest route is to go to Salle Couverte Al Andalous. It's 2.3 km away. I've sent the location to your map. Do you need help with anything else?\"",
                voiceLabel: "Voice Message (Darija)"
            },
            cta: "See how Smart Safe Checkpoints work"
        },
        tech: {
            badge: "TECHNOLOGY STACK",
            titlePrefix: "The ",
            titleHighlight: "Core Technology",
            description: "Built from the ground up for maximum performance and reliability. Every layer engineered for Morocco's unique challenges.",
            architecture: "System Architecture",
            layers: {
                app: "CROSS-PLATFORM APP",
                frontend: "FRONTEND (WEB)",
                sync: "LOCAL-FIRST SYNC",
                ai: "SOVEREIGN AI",
                infra: "ACTIVE INFRA",
                mesh: "MESH NETWORKING",
                map: "TACTICAL MAP",
                squad: "SQUAD GRID"
            },
            security: {
                title: "Security & Compliance",
                desc: "Full compliance with Moroccan Law 09-08. CNDP oversight from day one."
            }
        },
        market: {
            badge: "MARKET OPPORTUNITY",
            titlePrefix: "Morocco ",
            titleHighlight: "2030",
            description: "A unique convergence of market readiness, government investment, and global events creates an unprecedented opportunity.",
            tamTitle: "Total Addressable Market",
            drivers: {
                fifa: { title: "FIFA World Cup 2030", desc: "High-tech crowd safety and instant missing-person tracking required" },
                gov: { title: "Government Investment", desc: "MAD 200M+ in early warning systems + MAD 1.3B in smart city cameras" },
                ai: { title: "AI Market Gap", desc: "No Moroccan-trained safety AI exists. We will be the first." },
                disaster: { title: "Disaster-Prone Region", desc: "Earthquakes, floods, wildfires — urgent need for early warning" }
            },
            revenue: {
                title: "Revenue Streams",
                freemium: "Freemium (Basic)",
                premium: "Premium Subscription",
                family: "Family Plan",
                bridge: "NAJA Bridge",
                core: "NAJA CORE"
            },
            roadmap: "Roadmap 2026–2030",
            partnership: {
                title: "Inwi Partnership Opportunity",
                subtitle: "Building Morocco's digital nervous system together"
            },
            cta: {
                discuss: "DISCUSS PARTNERSHIP",
                deck: "VIEW FULL PITCH DECK"
            }
        },
        team: {
            badge: "THE TEAM",
            titlePrefix: "Meet The ",
            titleHighlight: "Founder",
            description: "The driving force behind NAJA AI's mission to achieve zero casualties from preventable disasters.",
            founder: {
                role: "Founder & CTO",
                subtitle: "Systems Architect & Sovereign AI Specialist",
                bio: "Aymane is the architect behind Morocco's Sovereign Neural Link. Combining 9+ years of full-stack engineering with deep expertise in AI agent orchestration, he built NAJA AI to solve a critical national failure: the lack of predictive, resilient safety infrastructure during the \"Poly-Crisis\" of floods and drought.",
                vision: "We don't just react to disasters. We engineer the national reflex that prevents them.",
                skills: "Core Skills",
                achievements: "Key Achievements"
            },
            hiring: {
                title: "Core Team (To Be Expanded with Funding)",
                roles: {
                    ai: "AI/ML Engineers",
                    embedded: "Embedded Systems Engineers",
                    backend: "Backend Developers",
                    sales: "Sales & Partnerships Lead"
                }
            },
            ip: {
                title: "Intellectual Property",
                desc: "Provisional patents filed for our core innovations:"
            }
        },
        contact: {
            badge: "PARTNER WITH NAJA AI",
            titlePrefix: "Ready to Build ",
            titleHighlight: "Morocco's Sovereign Grid?",
            description: "Every second counts. Join us in deploying Metronim sensors and Zelli Angel to secure our water infrastructure. From intelligent dam release to sovereign AI training, we are building the future.",
            ask: "The Ask",
            nextSteps: "Next Steps",
            form: {
                title: "Send a Message",
                name: "Name",
                email: "Email",
                org: "Organization",
                interest: "Interest",
                message: "Message",
                submit: "SEND MESSAGE",
                success: "Message Sent!",
                successDesc: "Thank you for your interest. We'll get back to you within 24 hours."
            },
            finalCta: {
                title: "We Track. We Predict. We Protect.",
                desc: "Be part of Morocco's answer to disaster preparedness. Together, we can achieve zero casualties from preventable disasters.",
                partner: "TAKE MY MONEY — LET'S PARTNER",
                deck: "VIEW PITCH DECK"
            }
        }
    },
    ar: {
        // Navbar
        nav: {
            problem: "المشكلة",
            solution: "الحل",
            zelliAngel: "الملاك زلي",
            technology: "التقنية",
            market: "السوق",
            team: "الفريق",
            pitchDeck: "العرض الاستثماري",
            investNow: "استثمر الآن",
            subtitle: "الشبكة الوطنية للسلامة"
        },
        footer: {
            rights: "© 2026 NAJA AI. جميع الحقوق محفوظة.",
            contact: "اتصل بنا",
            pitchDeck: "العرض التقديمي",
            builtWith: "صنع بـ ❤️ في المغرب"
        },
        loader: {
            text: "جاري تحميل شبكة الناجا..."
        },
        // Hero
        hero: {
            loading: "جار تحميل شبكة ناجا...",
            alert: "⚠️ تنبيه نشط: تحذير من الفيضانات المفاجئة - سهل الغرب / القصر الكبير",
            title: { naja: "ناجا", ai: " للذكاء الاصطناعي" },
            subtitle: "الشبكة الوطنية للسلامة",
            tagline: { track: "نحن نتتبع.", predict: "نحن نتنبأ.", protect: "نحن نحمي." },
            description: "أول نظام تشغيل موحد للسلامة في المغرب — يربط الهواتف الذكية والأجهزة القابلة للارتداء وأجهزة التوجيه لحماية العائلات، والتنبؤ بالكوارث، وتحديد مكان المفقودين في الوقت الفعلي.",
            capabilities: {
                track: { title: "نحن نتتبع", subtitle: "الموقع في الوقت الفعلي", desc: "مزامنة عبر الأجهزة بتأخير أقل من 50 مللي ثانية. مراقبة حيوية لمعدل ضربات القلب والسقوط والإجهاد.", features: ['شبكة متداخلة', 'الوضع غير المتصل', 'مزامنة الأجهزة'] },
                predict: { title: "نحن نتنبأ", subtitle: "ذكاء اصطناعي مدرب على المغرب", desc: "نماذج خاصة تتنبأ بالفيضانات في وديان محددة، وتحليل ديناميكيات الحشود لكأس العالم 2030.", features: ['تنبؤ بالفيضانات', 'تحليل الحشود', 'نقاط الحقيقة™'] },
                protect: { title: "نحن نحمي", subtitle: "استجابة آلية", desc: "تنبيهات فورية، شبكات بحث عن الأطفال المفقودين، بروتوكولات طوارئ تنشط في ثوانٍ.", features: ['SOS بلمسة واحدة', 'وضع الزومبي', 'نقاط تفتيش ذكية'] }
            },
            cta: { partner: "كن شريكاً معنا", explore: "استكشف الرؤية" },
            partner: "بالشراكة مع إنوي لبناء الجهاز العصبي الرقمي للمغرب",
            scroll: "اكتشف المزيد"
        },
        // Checkpoints
        checkpoints: {
            tag: "نقاط أمان ذكية",
            titlePrefix: "بوصلتك في",
            titleHighlight: "وقت الأزمات",
            description: "في الأزمات، يعم الذعر. تقوم ناجا بتحويل الفوضى إلى نظام من خلال تحديد نقاط أمان موثقة ديناميكيًا مع تتبع السعة في الوقت الفعلي.",
            verified: " نقاط أمان موثقة",
            liveFeed: "بث حي عبر الأقمار الصناعية",
            safestPattern: "تم رصد المسار الآمن",
            fast: "سريع",
            nearbySanctuaries: "الملاذات القريبة",
            updated: "تم التحديث قبل ثانيتين",
            walk: "مشي",
            currentCapacity: "السعة الحالية",
            startNav: "ابدأ الملاحة",
            entryPass: "عرض تصريح الدخول",
            you: "أنت",
            status: {
                open: "مفتوح وآمن",
                nearing: "قارب على الامتلاء",
                full: "ممتلئ / الطريق مغلق",
                available: "متاح",
                filling: "يمتلئ بسرعة",
                capacity: "ممتلئ تماماً",
                mesh: "الشبكة الشعرية نشطة"
            }
        },
        // App Simulation
        simulation: {
            header: "الخريطة التكتيكية",
            hazard: "منطقة خطر",
            safe: "منطقة آمنة",
            sanctuary: "ملاذ آمن",
            you: "أنت",
            squad: "الفريق",
            actions: {
                navigate: "ملاحة",
                report: "إبلاغ",
                findSquad: "البحث عن الفريق",
                broadcast: "بث نداء"
            },
            mvpReady: "جاهز للاستخدام",
            titlePrefix: "شاهد ",
            titleHighlight: "التطبيق يعمل",
            description: "النسخة الأولية جاهزة وتعمل. استكشف الخريطة التكتيكية، شبكة حماية العائلة، وأدلة النجاة المدعومة بالذكاء الاصطناعي التي ستنقذ الأرواح في المغرب.",
            squadStatus: "حالة الفريق",
            survivalGuides: "أدلة النجاة",
            features: {
                map: { title: "خريطة المخاطر التكتيكية", desc: "تصور فوري لتهديدات الزلازل والحرارة والأرصاد الجوية. بيانات رسمية مدمجة مع تقارير الجماهير الموثقة." },
                squad: { title: "شبكة حماية الفريق", desc: "تتبع أفراد العائلة بإشارات 'نبض' منخفضة النطاق. تعمل عبر شبكة بلوتوث عندما تكون الشبكات الخلوية معطلة." },
                ai: { title: "حارس جيميني الذكي", desc: "خبير نجاة في جيبك. يقدم نصائح محلية دقيقة وتوعية باللهجة المغربية (الدارجة) أثناء الفوضى." },
                offline: { title: "الوضع غير المتصل", desc: "بروتوكولات النجاة الحرجة مخزنة محليًا. تعمل بدون إنترنت عندما تكون في أمس الحاجة إليها." }
            }
        },
        problem: {
            badge: "المشكلة",
            titlePrefix: "أنظمة سلامة ",
            titleHighlight: "مفككة",
            description: "يواجه المغرب كوارث متكررة بأنظمة تفاعلية تكلف الأرواح. كشفت فيضانات الغرب وزلزال الأطلس عن فجوات خطيرة في استعدادنا الوطني.",
            realDisasters: "كوارث حقيقية",
            rootCauses: "الأسباب الجذرية",
            impact: {
                title: "ثمن التقاعس",
                text: "كل كارثة تكشف نفس النمط: تحذيرات متأخرة، إخلاء فوضوي، عائلات مشتتة، وخسائر كان يمكن تجنبها. المغرب يستحق الأفضل. بنينا ناجا لتكون الجواب."
            },
            cases: {
                ksar: {
                    title: 'القصر الكبير: "مدينة الأشباح"',
                    location: 'العرائش / سهل الغرب، المغرب',
                    desc: 'وصل سد واد المخازن إلى 166% من طاقته. الإطلاق الاضطراري أغرق سهل الغرب بالكامل. أصبحت المدينة "مدينة أشباح" مع هروب 85% من السكان في ذعر.'
                },
                gharb: {
                    title: 'أزمة سد سهل الغرب',
                    location: 'سد واد المخازن',
                    desc: 'كان إطلاق السد حتمياً، لكن الضرر لم يكن كذلك. بدون أنظمة إنذار مبكر، لم يكن لدى المزارعين وقت لنقل الماشية أو المعدات إلى أرض مرتفعة.'
                },
                atlas: {
                    title: 'زلزال الحوز',
                    location: 'جبال الأطلس، المغرب',
                    desc: 'ضرب زلزال بقوة 6.8 درجة جبال الأطلس في الساعة 11:11 مساءً. انقطعت القرى النائية لأيام. المأساة التي ألهمت ناجا.'
                }
            },
            causes: {
                goldenHour: { title: "ضياع الساعة الذهبية", desc: "أكثر من 2,000 طفل يبلغ عن فقدانهم في المغرب سنوياً (DGSN 2025) – معظمهم بدون أدوات بحث رقمية." },
                dataSilos: { title: "صوامع البيانات", desc: "المقاييس الحيوية، الموقع، والتنبيهات لا تتزامن أبداً عبر الأجهزة. العائلات عمياء عندما تكون الدقائق مهمة." },
                noGrid: { title: "غياب الشبكة الوطنية", desc: "البنية التحتية الحالية (رادارات، كاميرات) معزولة عن المواطنين. لا نظام تنبيه موحد." },
                aiFail: { title: "فشل الذكاء الاصطناعي العام", desc: "النماذج الدولية لا تفهم أنماط الفيضانات المحلية، أو بؤر الجريمة، أو الاتجاهات الصحية." },
                chaos: { title: "فوضى المعلومات", desc: "أثناء الكوارث، يواجه المواطنون أخباراً غير مؤكدة وتقارير مزيفة. لا مصدر موثوق للتوجيه." }
            }
        },
        solution: {
            badge: "الحل",
            titlePrefix: "نظام ",
            titleHighlight: "ناجا البيئي",
            description: "أول نظام تشغيل موحد للسلامة في المغرب — يربط جميع أجهزتك في شبكة حماية واحدة.",
            categories: {
                track: "نحن نتتبع",
                predict: "نحن نتوقع",
                protect: "نحن نحمي"
            },
            cta: "تعرف على الملاك زلي، حارسنا الذكي",
            caseStudy: {
                title: "دراسة حالة: إعادة تصور القصر الكبير",
                subtitle: "ماذا لو كانت ناجا نشطة خلال فيضانات فبراير 2026؟",
                phase1: { title: "الذكاء التنبؤي", desc: "ناجا متصلة بمستشعرات ABHS تكشف ارتفاع التدفق. محرك المحاكاة يتوقع فيضان القطاع ب." },
                phase2: { title: "تنبيه محلي فائق الدقة", desc: "الملاك زلي يبدأ مكالمات سريعة لجميع الهواتف في المنطقة الحمراء بصوت آلي بالدارجة." },
                phase3: { title: "الإخلاء التكتيكي", desc: "تطبيق ناجا يتحول لوضع الإخلاء. توجيه أكثر من 5,000 شخص إلى 3 نقاط تفتيش في 45 دقيقة." },
                result: { title: "النتيجة المحاكية مع ناجا", quote: "\"مع ناجا، تتحول فوضى الأزمة إلى هجرة منظمة.\"" }
            }
        },
        zelli: {
            badge: "الملاك زلي",
            titlePrefix: "القلب ",
            titleHighlight: "الذكي لناجا",
            description: "ليس مجرد روبوت دردشة — حارس استباقي يتواصل بلغتك عبر الصوت والنص، 24/7.",
            features: {
                guardian: { title: "حارس شخصي 24/7", desc: "يراقب التهديد ويرسل تنبيهات صوتية شخصية بلغتك.", example: "\"أيمن، متوقع فيضان في منطقتك خلال ساعتين. جهزت لك طريقاً آمناً...\"" },
                mass: { title: "اتصال جماعي", desc: "يتصل أو يراسل آلاف المستخدمين المتضررين تلقائياً عبر واجهة برمجة المكالمات السريعة.", example: "تجاوز وضع \"عدم الإزعاج\" لإيقاظ السكان أثناء الطوارئ." },
                evac: { title: "توجيه الإخلاء", desc: "يتحول إلى \"وضع الإخلاء\" أثناء الكوارث، موجهاً كل مستخدم خطوة بخطوة.", example: "إعادة توجيه ديناميكية بناءً على الظروف الحالية إلى نقاط التفتيش الآمنة." },
                whatsapp: { title: "تكامل واتساب", desc: "أرسل 'فين نمشي؟' واحصل على رسالة صوتية فورية بأكثر الطرق أماناً.", example: "لا حاجة لتطبيق للتوجيه الأساسي — يعمل على أي هاتف به واتساب." }
            },
            demo: {
                title: "تجربة محادثة حية",
                subtitle: "الملاك زلي يستجيب لتنبيه فيضان",
                userMsg: "فين نمشي؟",
                aiMsg: "\"أيمن، كاين تحذير من فيضان في منطقتك. الطريق الأكثر أماناً هو التوجه للقاعة المغطاة الأندلس. بعيدة بـ 2.3 كم. صيفطت ليك الموقع في الخريطة. محتاج مساعدة أخرى؟\"",
                voiceLabel: "رسالة صوتية (الدارجة)"
            },
            cta: "شاهد كيف تعمل نقاط التفتيش الآمنة"
        },
        tech: {
            badge: "حزمة التقنية",
            titlePrefix: "الخلطة ",
            titleHighlight: "السرية",
            description: "بنيت من الصفر لأقصى أداء وموثوقية. كل طبقة هندست لتحديات المغرب الفريدة.",
            architecture: "هندسة النظام",
            layers: {
                app: "تطبيق متعدد المنصات",
                frontend: "الواجهة الأمامية (WEB)",
                sync: "مزامنة محلية أولاً",
                ai: "ذكاء اصطناعي سيادي",
                infra: "بنية تحتية نشطة",
                mesh: "شبكات متداخلة (Mesh)",
                map: "خريطة تكتيكية",
                squad: "شبكة الفريق"
            },
            security: {
                title: "الأمن والامتثال",
                desc: "امتثال كامل للقانون المغربي 09-08. إشراف CNDP من اليوم الأول."
            }
        },
        market: {
            badge: "فرصة السوق",
            titlePrefix: "المغرب ",
            titleHighlight: "2030",
            description: "تقارب فريد بين جاهزية السوق، الاستثمار الحكومي، والأحداث العالمية يخلق فرصة غير مسبوقة.",
            tamTitle: "إجمالي السوق المتاح",
            drivers: {
                fifa: { title: "كأس العالم 2030", desc: "مطلوب سلامة حشود عالية التقنية وتتبع فوري للمفقودين" },
                gov: { title: "الاستثمار الحكومي", desc: "أكثر من 200 مليون درهم في أنظمة الإنذار المبكر + 1.3 مليار في كاميرات المدن الذكية" },
                ai: { title: "فجوة سوق الذكاء الاصطناعي", desc: "لا يوجد ذكاء اصطناعي للسلامة مدرب مغربياً. سنكون الأول." },
                disaster: { title: "منطقة عرضة للكوارث", desc: "زلازل، فيضانات، حرائق غابات — حاجة ملحة للإنذار المبكر" }
            },
            revenue: {
                title: "تدفقات الإيرادات",
                freemium: "مجاني (أساسي)",
                premium: "اشتراك مميز",
                family: "خطة العائلة",
                bridge: "جسر ناجا",
                core: "ناجا كور"
            },
            roadmap: "خارطة الطريق 2026–2030",
            partnership: {
                title: "فرصة شراكة إنوي",
                subtitle: "بناء الجهاز العصبي الرقمي للمغرب معاً"
            },
            cta: {
                discuss: "ناقش الشراكة",
                deck: "عرض ملف الاستثمار"
            }
        },
        team: {
            badge: "الفريق",
            titlePrefix: "تعرف على ",
            titleHighlight: "المؤسس",
            description: "القوة الدافعة وراء مهمة ناجا لتحقيق صفر ضحايا من الكوارث التي يمكن تجنبها.",
            founder: {
                role: "المؤسس والمدير التقني",
                subtitle: "مهندس نظم ومتخصص في الذكاء الاصطناعي السيادي",
                bio: "أيمن هو المهندس وراء الرابط العصبي السيادي للمغرب. بجمع أكثر من 9 سنوات من الهندسة الشاملة مع خبرة عميقة في تنسيق وكلاء الذكاء الاصطناعي، بنى ناجا لحل فشل وطني حرج: نقص البنية التحتية للسلامة التنبؤية والمرنة خلال \"الأزمة المتعددة\" للفيضانات والجفاف.",
                vision: "نحن لا نكتفي برد الفعل تجاه الكوارث. نحن نهندس رد الفعل الوطني الذي يمنعها.",
                skills: "المهارات الأساسية",
                achievements: "إنجازات رئيسية"
            },
            hiring: {
                title: "الفريق الأساسي (سيتم توسيعه مع التمويل)",
                roles: {
                    ai: "مهندسو ذكاء اصطناعي/تعلم آلي",
                    embedded: "مهندسو أنظمة مدمجة",
                    backend: "مطورو واجهة خلفية",
                    sales: "رئيس المبيعات والشراكات"
                }
            },
            ip: {
                title: "الملكية الفكرية",
                desc: "براءات اختراع مؤقتة مقدمة لابتكاراتنا الأساسية:"
            }
        },
        contact: {
            badge: "شارك مع ناجا",
            titlePrefix: "جاهز لبناء ",
            titleHighlight: "شبكة المغرب السيادية؟",
            description: "كل ثانية تهم. انضم إلينا في نشر مستشعرات مترونيم والملاك زلي لتأمين بنيتنا التحتية المائية. من إطلاق السدود الذكي إلى تدريب الذكاء الاصطناعي السيادي، نحن نبني المستقبل.",
            ask: "الطلب",
            nextSteps: "الخطوات التالية",
            form: {
                title: "أرسل رسالة",
                name: "الاسم",
                email: "البريد الإلكتروني",
                org: "المؤسسة",
                interest: "الاهتمام",
                message: "الرسالة",
                submit: "أرسل الرسالة",
                success: "تم إرسال الرسالة!",
                successDesc: "شكراً لاهتمامك. سنرد عليك خلال 24 ساعة."
            },
            finalCta: {
                title: "نحن نتتبع. نحن نتوقع. نحن نحمي.",
                desc: "كن جزءاً من إجابة المغرب للاستعداد للكوارث. معاً، يمكننا تحقيق صفر ضحايا من الكوارث التي يمكن تجنبها.",
                partner: "خذ نقودي — لنكن شركاء",
                deck: "عرض ملف الاستثمار"
            }
        }
    }
};
